/**
 * Translate raw error strings from mediabunny / MediaWiki / browser network
 * APIs into user-facing actionable messages.
 *
 * The previous error-handling path surfaced strings like "Cannot convert
 * this file: video: codec not supported in container" verbatim, which
 * tells the user nothing useful. This module maps the most common cases
 * to plain-language guidance with concrete next steps, while leaving
 * unknown errors to fall through to the original message.
 *
 * Each entry returns an i18n key + optional params; the caller calls t().
 * That keeps translation pluggable instead of baking English strings here.
 */

export interface FriendlyError {
  /** i18n key the caller should pass to t(). */
  key: string
  /** Optional named params for the translation. */
  params?: Record<string, string | number>
  /** The original error message, in case the caller wants to log/expose
   *  it for diagnostics. Always non-empty. */
  raw: string
}

/** Match a raw error message against known patterns and return the
 *  friendly key. Falls back to errors.unknown with the raw message. */
export function classifyError(err: unknown): FriendlyError {
  const raw = err instanceof Error ? err.message : String(err ?? "")
  const lower = raw.toLowerCase()

  // --- Chunk-level network failures with resume hints ----------------

  // useUploader throws errors with a "Chunk N/M failed" prefix when a
  // single chunk in a chunkedUpload run gives up after all retries. The
  // user's stash filekey is still alive (~6h) and the resume banner will
  // pick it up on next visit, so steer them there.
  if (/chunk\s+\d+\s*\/\s*\d+\s+failed/i.test(raw)) {
    return {
      key: "errors.chunk_failed_resumable",
      raw,
      params: { msg: raw }
    }
  }

  // --- Encoder / mediabunny errors -----------------------------------

  if (/cannot convert this file/i.test(raw) ||
      /codec not supported in container/i.test(raw)) {
    return { key: "errors.encode_unsupported_codec", raw }
  }
  if (/no video track/i.test(raw)) {
    return { key: "errors.encode_no_video", raw }
  }
  if (/no audio track/i.test(raw) && /required/i.test(raw)) {
    return { key: "errors.encode_no_audio", raw }
  }
  if (/conversion produced no output/i.test(raw) ||
      /produced no output buffer/i.test(raw)) {
    return { key: "errors.encode_empty_output", raw }
  }
  if (/cancelled/i.test(raw) && raw.length < 32) {
    return { key: "errors.cancelled", raw }
  }

  // --- MediaWiki upload errors --------------------------------------

  if (/maxlag/.test(lower)) {
    return { key: "errors.upload_maxlag", raw }
  }
  if (/ratelimit/i.test(raw)) {
    return { key: "errors.upload_ratelimit", raw }
  }
  if (/csrf|badtoken|invalid.*token/i.test(raw)) {
    return { key: "errors.upload_csrf", raw }
  }
  if (/upload-too-many-redirects/i.test(raw) ||
      /verification-error/i.test(raw)) {
    return { key: "errors.upload_stash_lost", raw }
  }
  if (/file is empty|filesize.*0/i.test(raw)) {
    return { key: "errors.upload_empty_file", raw }
  }
  if (/file with this name exists/i.test(raw) ||
      /fileexists/i.test(raw)) {
    return { key: "errors.upload_exists", raw }
  }
  if (/protected/i.test(lower) && /title/i.test(lower)) {
    return { key: "errors.upload_title_protected", raw }
  }
  if (/blocked/i.test(lower) && /from editing/i.test(lower)) {
    return { key: "errors.upload_blocked", raw }
  }
  if (/upload rights/i.test(raw)) {
    return { key: "errors.no_upload_rights", raw }
  }
  if (/autoconfirmed/i.test(raw)) {
    return { key: "errors.not_autoconfirmed", raw }
  }
  if (/anonymous/i.test(lower) && /token/i.test(lower)) {
    return { key: "errors.session_expired", raw }
  }

  // --- HTTP / network errors ----------------------------------------

  if (/^http\s+5\d\d/i.test(raw) || /service unavailable|server error/i.test(raw)) {
    return { key: "errors.http_5xx", raw }
  }
  if (/^http\s+4\d\d/i.test(raw)) {
    return { key: "errors.http_4xx", raw, params: { msg: raw } }
  }
  if (/networkerror|failed to fetch|load failed/i.test(raw)) {
    return { key: "errors.network", raw }
  }
  if (/aborted/i.test(raw)) {
    return { key: "errors.cancelled", raw }
  }

  // --- OAuth ---------------------------------------------------------

  if (/oauth.*state mismatch|csrf/i.test(raw) && /oauth/i.test(lower)) {
    return { key: "errors.oauth_state_mismatch", raw }
  }
  if (/oauth.*pkce verifier/i.test(raw)) {
    return { key: "errors.oauth_pkce_lost", raw }
  }
  if (/token exchange failed/i.test(raw)) {
    return { key: "errors.oauth_exchange_failed", raw, params: { msg: raw } }
  }

  return { key: "errors.unknown", raw, params: { msg: raw } }
}
